// ============================================================
// TenantDetailPage.js
// Route /tenants/:tenantId — single-Tenant management surface.
//
// Mirrors ClientDetailPage section-by-section:
//   <PageHeader> (Tenant name)
//   <TenantDetailMeta> (IdentityProvider badge, IsActive badge,
//                       stat row with clickable Clients link)
//   <Tabs> (Federation today; Phase 2 adds more without refactor)
//   <FederationTab> — Tenant-scoped federation surface
//
// URL state:
//   ?tab=federation  (default: federation; only tab today)
//
// Header endpoint is fetched once on mount / tenantId change. The
// active tab is responsible for its own fetching.
//
// 404 handling: inline (not via global toast) — operators routinely
// arrive here from a stale link.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Tabs from '../components/Tabs';
import { IdentityProviderBadge, ActiveStatusBadge } from '../components/StatusBadge';
import { getAdminTenantDetail } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import FederationTab from './FederationTab';
import EditTenantDialog from './EditTenantDialog';
import StatusMessage from '../components/StatusMessage';
import './TenantDetailPage.css';

const TAB_IDS = ['federation'];
const DEFAULT_TAB = 'federation';

const TAB_DEFS = [
  { id: 'federation', label: 'Federation' },
];

export default function TenantDetailPage() {
  const { tenantId: tenantIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantId = parseInt(tenantIdParam, 10);
  const isValidId = Number.isInteger(tenantId) && tenantId > 0;

  const rawTab = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(rawTab) ? rawTab : DEFAULT_TAB;

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Edit dialog (2026-09-03). Rendered conditionally so its form state
  // is rebuilt from the freshly fetched tenant on every open.
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchHeader = useCallback(async () => {
    if (!isValidId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getAdminTenantDetail(tenantId);
      setTenant(data);
    } catch (err) {
      if (err && err.status === 404) {
        setNotFound(true);
      } else {
        setError(translateError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, isValidId]);

  useEffect(() => { fetchHeader(); }, [fetchHeader]);

  function handleTabChange(nextTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextTab === DEFAULT_TAB) next.delete('tab'); else next.set('tab', nextTab);
      return next;
    });
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <StatusMessage variant="loading" className="tenant-detail__state">Loading tenant…</StatusMessage>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="Tenant not found" />
        <StatusMessage variant="empty" className="tenant-detail__state tenant-detail__state--empty">
          <p>That Tenant does not exist or has been removed.</p>
          <Link to="/tenants" className="tenant-detail__back-link">
            ← Return to Tenants list
          </Link>
        </StatusMessage>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Tenant" />
        <StatusMessage variant="error" className="tenant-detail__state tenant-detail__state--error">
          <p>{error}</p>
          <button type="button" className="tenant-detail__retry" onClick={fetchHeader}>
            Retry
          </button>
        </StatusMessage>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={tenant.tenantName}
        cta={
          <button
            type="button"
            className="tenant-detail__edit"
            onClick={() => setIsEditOpen(true)}
          >
            <Pencil size={16} aria-hidden="true" />
            <span>Edit tenant</span>
          </button>
        }
      />

      {isEditOpen && (
        <EditTenantDialog
          tenant={tenant}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSaved={() => fetchHeader()}
        />
      )}

      <div className="tenant-detail__meta" data-testid="tenant-detail-meta">
        <IdentityProviderBadge provider={tenant.identityProvider} />
        <ActiveStatusBadge isActive={tenant.isActive} />
        <span className="tenant-detail__stats">
          <Link
            to={`/clients?tenantId=${tenantId}`}
            className="tenant-detail__stat-link"
            data-testid="tenant-detail-clients-link"
          >
            {tenant.clientCount} {tenant.clientCount === 1 ? 'Client' : 'Clients'}
          </Link>
          {' · '}
          <span>{tenant.activeUserCount} active {tenant.activeUserCount === 1 ? 'user' : 'users'}</span>
          {' · '}
          <span>{tenant.invitationsPendingCount} pending {tenant.invitationsPendingCount === 1 ? 'invitation' : 'invitations'}</span>
        </span>
      </div>

      <Tabs tabs={TAB_DEFS} active={activeTab} onChange={handleTabChange} />

      <div className="tenant-detail__tab-content">
        {activeTab === 'federation' && (
          <FederationTab
            tenantId={tenantId}
            tenantExternalTenantId={tenant.externalTenantId}
          />
        )}
      </div>
    </>
  );
}

export { TAB_IDS, DEFAULT_TAB };
